"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.canTransition = canTransition;
exports.activeVersion = activeVersion;
exports.isSuperseded = isSuperseded;
exports.isAcceptanceStale = isAcceptanceStale;
exports.staleAcceptanceCount = staleAcceptanceCount;
/**
 * The version state machine, plus the values the spec DERIVES rather than
 * stores (§5.3). Deriving them removes a scheduler and a batch sweep, and with
 * them the class of bugs where a flag disagrees with reality.
 */
/**
 * `published` is TERMINAL. There is no stored `superseded` status: see
 * `isSuperseded` below and the note on VERSION_STATUSES in ./documents.
 */
const ALLOWED = {
    draft: ['in_review'],
    in_review: ['approved', 'draft'],
    approved: ['published', 'draft'],
    published: [],
};
function canTransition(from, to) {
    return ALLOWED[from].includes(to);
}
/**
 * The active version is the PUBLISHED version with the greatest effectiveAt at
 * or before `nowIso`. A version published with a future effective date is not
 * yet active — the previous one still governs. Nothing is stored; nothing drifts.
 */
function activeVersion(versions, nowIso) {
    const now = Date.parse(nowIso);
    let best = null;
    let bestAt = -Infinity;
    for (const v of versions) {
        if (v.status !== 'published' || !v.effectiveAt)
            continue;
        const at = Date.parse(v.effectiveAt);
        if (Number.isNaN(at) || at > now)
            continue;
        // Tie-break on versionNo so two versions sharing an instant resolve deterministically.
        if (at > bestAt || (at === bestAt && best !== null && v.versionNo > best.versionNo)) {
            best = v;
            bestAt = at;
        }
    }
    return best;
}
/**
 * Superseded is DERIVED, never stored — a published version is superseded once
 * a later published version has taken effect. Storing it would require a
 * scheduler and would remove the version from activeVersion()/isAcceptanceStale().
 */
function isSuperseded(versionNo, versions, nowIso) {
    const active = activeVersion(versions, nowIso);
    return !!active && active.versionNo > versionNo;
}
/**
 * A party sitting at `acceptedVersionNo` is stale only if a MATERIAL version
 * took effect after theirs, at or before now. A run of minor changes never
 * forces re-acceptance.
 */
function isAcceptanceStale(acceptedVersionNo, versions, nowIso) {
    const active = activeVersion(versions, nowIso);
    if (!active)
        return false;
    if (acceptedVersionNo >= active.versionNo)
        return false;
    const now = Date.parse(nowIso);
    return versions.some((v) => v.status === 'published' &&
        v.changeType === 'material' &&
        v.versionNo > acceptedVersionNo &&
        v.versionNo <= active.versionNo &&
        !!v.effectiveAt &&
        Date.parse(v.effectiveAt) <= now);
}
/** The number shown on the publish screen BEFORE publishing. Spec §5.4 item 5. */
function staleAcceptanceCount(acceptances, versions, nowIso) {
    return acceptances.filter((a) => isAcceptanceStale(a.versionNo, versions, nowIso)).length;
}
//# sourceMappingURL=documentLifecycle.js.map